import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../../utils/structuredData';
import './ServicePage.css';

const FlutterAppDevelopment = () => {
  const serviceStructuredData = serviceSchema(
    'Flutter App Development',
    'Cross-platform mobile app development with Flutter. Build beautiful, high-performance apps for both Android and iOS from a single codebase. Affordable Flutter app development services in Bhopal, Indore, and across India.',
    'Mobile App Development'
  );

  const breadcrumbStructuredData = breadcrumbSchema([
    { name: 'Home', url: 'https://www.gettechupdate.com' },
    { name: 'Services', url: 'https://www.gettechupdate.com/#services' },
    { name: 'Flutter App Development', url: 'https://www.gettechupdate.com/services/flutter-app-development' }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEO
        title="Flutter App Development Services in Bhopal, Indore | Affordable Mobile App Development"
        description="Professional Flutter app development services in Bhopal, Indore, Delhi, Mumbai, Bangalore, and across India. Build cross-platform mobile apps for Android and iOS at budget-friendly prices. Expert Flutter developers."
        keywords="Flutter app development, Flutter development services, cross-platform app development, mobile app development in Bhopal, mobile app development in Indore, Flutter app development in Delhi, Flutter app development in Mumbai, Flutter app development in Bangalore, affordable Flutter development, budget Flutter app development, Flutter developers in Bhopal, Flutter developers in Indore, Flutter app development company, mobile app development in budget, Flutter development services India"
        url="https://www.gettechupdate.com/services/flutter-app-development"
        structuredData={combinedSchema}
      />
      <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-icon-large">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <h1>Flutter App Development</h1>
          <p className="service-hero-subtitle">
            Build beautiful, high-performance cross-platform mobile apps for Android and iOS from a single codebase. 
            Affordable Flutter app development services in Bhopal, Indore, and across India.
          </p>
        </div>
      </div>

      <div className="service-content">
        <section className="service-section">
          <h2>Professional Flutter App Development Services</h2>
          <p>
            Flutter is Google's powerful UI framework that allows you to build native mobile applications for both 
            Android and iOS from a single codebase. We offer expert Flutter app development services in Bhopal, Indore, 
            Delhi, Mumbai, Bangalore, Pune, Hyderabad, Chennai, Kolkata, Jaipur, and across India at budget-friendly prices. 
            Our team creates stunning, high-performance mobile apps that deliver exceptional user experiences while saving 
            you time and development costs.
          </p>
        </section>

        <section className="service-section">
          <h2>Why Choose Flutter for Your Mobile App?</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-code"></i>
              <h3>Single Codebase</h3>
              <p>Write once, deploy on both Android and iOS. Save up to 50% development time and costs compared to native development.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-tachometer-alt"></i>
              <h3>High Performance</h3>
              <p>Flutter apps compile to native code, delivering near-native performance with smooth 60fps animations.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-paint-brush"></i>
              <h3>Beautiful UI</h3>
              <p>Rich, customizable widgets and Material Design components for creating stunning user interfaces.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-dollar-sign"></i>
              <h3>Cost-Effective</h3>
              <p>Affordable mobile app development solutions perfect for startups and small businesses on a budget.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-sync"></i>
              <h3>Hot Reload</h3>
              <p>See changes instantly with Flutter's hot reload feature, speeding up development and iteration.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shield-alt"></i>
              <h3>Google Backed</h3>
              <p>Built and maintained by Google, ensuring long-term support and continuous improvements.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Flutter Development Services</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-mobile-alt"></i>
              <h3>Cross-Platform Apps</h3>
              <p>Build apps that work seamlessly on both Android and iOS with a single codebase, reducing development time and costs.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shopping-cart"></i>
              <h3>E-commerce Apps</h3>
              <p>Feature-rich e-commerce mobile apps with shopping carts, payment integration, and order management.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-users"></i>
              <h3>Social Media Apps</h3>
              <p>Engaging social networking apps with real-time features, chat functionality, and content sharing.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-utensils"></i>
              <h3>Food Delivery Apps</h3>
              <p>Complete food delivery solutions with restaurant listings, ordering, tracking, and payment integration.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-graduation-cap"></i>
              <h3>Educational Apps</h3>
              <p>Interactive learning apps with video content, quizzes, progress tracking, and offline learning capabilities.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-heartbeat"></i>
              <h3>Healthcare Apps</h3>
              <p>Medical and healthcare apps with appointment booking, telemedicine, health tracking, and patient management.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-wallet"></i>
              <h3>Finance & Banking Apps</h3>
              <p>Secure financial apps with transaction management, bill payments, investment tracking, and banking features.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-gamepad"></i>
              <h3>Gaming Apps</h3>
              <p>Engaging mobile games with smooth animations, sound effects, and interactive gameplay.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-tools"></i>
              <h3>Custom App Development</h3>
              <p>Tailored Flutter apps designed specifically for your business needs and requirements.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Flutter Development Technologies</h2>
          <div className="tech-stack">
            <div className="tech-item">
              <i className="fas fa-mobile-alt"></i>
              <span>Flutter Framework</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-dart"></i>
              <span>Dart Language</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-fire"></i>
              <span>Firebase</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>SQLite</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-cloud"></i>
              <span>REST APIs</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-plug"></i>
              <span>Third-Party Plugins</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-chart-line"></i>
              <span>State Management</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-paint-brush"></i>
              <span>Material Design</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Flutter App Development in Major Cities</h2>
          <p>
            We provide professional Flutter app development services across India, with a strong presence in major cities:
          </p>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Bhopal</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Indore</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Delhi</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Mumbai</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Bangalore</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Pune</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Hyderabad</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Chennai</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Kolkata</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Jaipur</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Ahmedabad</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-map-marker-alt"></i>
              <span>Flutter App Development in Surat</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Flutter Development Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Requirement Analysis</h3>
              <p>We analyze your business needs, target audience, and app requirements to create a detailed project plan.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>UI/UX Design</h3>
              <p>Our designers create beautiful, intuitive interfaces that align with your brand and user expectations.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Flutter Development</h3>
              <p>Agile development with Flutter, using best practices, code reviews, and regular updates throughout the process.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Testing & QA</h3>
              <p>Comprehensive testing on both Android and iOS devices to ensure bug-free, high-performance apps.</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>App Store Deployment</h3>
              <p>We help you publish your app on Google Play Store and Apple App Store with proper optimization.</p>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <h3>Maintenance & Support</h3>
              <p>Ongoing maintenance, updates, bug fixes, and feature enhancements to keep your app running smoothly.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Why Choose Us for Flutter App Development?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Budget-Friendly Pricing</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Expert Flutter Developers</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Cross-Platform Expertise</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>On-Time Delivery</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Post-Launch Support</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Agile Methodology</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Quality Assurance</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Transparent Communication</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Scalable Solutions</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>NDA & Confidentiality</span>
            </div>
          </div>
        </section>

        <section className="service-cta">
          <h2>Ready to Build Your Flutter App?</h2>
          <p>Get affordable Flutter app development services in Bhopal, Indore, and across India</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get A Free Quote</Link>
            <Link to="/contact" className="btn-secondary">Schedule a Consultation</Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default FlutterAppDevelopment;

